import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Check, Mail } from 'lucide-react';

import { AuthShell } from './AuthShell';
import { supabase } from '../../lib/supabase';
import { logAudit } from '../../lib/audit';
import { cn } from '../../lib/cn';
import styles from './auth.module.css';

type Status = 'verifying' | 'success' | 'expired' | 'unsupported';

interface Params {
  error: string | null;
  type: string | null;
  accessToken: string | null;
}

function parseLocation(): Params {
  const hash = window.location.hash.replace(/^#/, '');
  const search = window.location.search.replace(/^\?/, '');
  const fromHash = new URLSearchParams(hash);
  const fromSearch = new URLSearchParams(search);
  const get = (k: string) => fromHash.get(k) ?? fromSearch.get(k);
  return {
    error: get('error'),
    type: get('type'),
    accessToken: get('access_token'),
  };
}

const SUCCESS_COUNTDOWN_S = 3;
const VERIFY_TIMEOUT_MS = 3000;

export function VerifyEmailScreen() {
  const navigate = useNavigate();
  const [params] = useState<Params>(() => parseLocation());
  const [status, setStatus] = useState<Status>('verifying');
  const [countdown, setCountdown] = useState(SUCCESS_COUNTDOWN_S);

  // Initial routing decision: error → expired; recovery → /auth/reset; otherwise verify.
  useEffect(() => {
    if (params.error) {
      setStatus('expired');
      return;
    }
    if (params.type === 'recovery') {
      // Preserve hash so ResetScreen / supabase auth-state listener can read tokens.
      navigate(`/auth/reset${window.location.hash}`, { replace: true });
      return;
    }
    if (!params.type && !params.accessToken) {
      setStatus('unsupported');
    }
    // Otherwise stay in 'verifying' — auth-state effect below confirms the session.
  }, [params, navigate]);

  // Watch for supabase to finish processing the access_token from the URL.
  useEffect(() => {
    if (status !== 'verifying') return;
    let active = true;
    let audited = false;
    const markVerified = () => {
      setStatus('success');
      if (!audited) {
        audited = true;
        void logAudit({ action: 'auth.email_verified' });
      }
    };

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return;
      if (event === 'PASSWORD_RECOVERY') {
        navigate(`/auth/reset${window.location.hash}`, { replace: true });
        return;
      }
      if (session) markVerified();
    });

    void supabase.auth.getSession().then(({ data }) => {
      if (active && data.session) markVerified();
    });

    const t = setTimeout(() => {
      if (!active) return;
      setStatus((s) => (s === 'verifying' ? 'expired' : s));
    }, VERIFY_TIMEOUT_MS);

    return () => {
      active = false;
      sub.subscription.unsubscribe();
      clearTimeout(t);
    };
  }, [status, navigate]);

  // Success countdown → /
  useEffect(() => {
    if (status !== 'success') return;
    if (countdown <= 0) {
      navigate('/', { replace: true });
      return;
    }
    const t = setTimeout(() => { setCountdown((c) => c - 1); }, 1000);
    return () => { clearTimeout(t); };
  }, [status, countdown, navigate]);

  if (status === 'verifying') {
    return (
      <AuthShell
        title="מאמת את החשבון..."
        brandSide={{
          title: 'רגע אחד',
          body: 'מאמת את הקישור שקיבלת באימייל.',
        }}
      >
        <div className={styles.verifyIcon}>
          <Mail size={36} />
        </div>
        <p className={cn(styles.sub, styles.verifyCenter)}>בודק את הקישור...</p>
      </AuthShell>
    );
  }

  if (status === 'success') {
    return (
      <AuthShell
        title="החשבון אומת בהצלחה"
        subtitle="ברוך הבא ל-FocusMode OS"
        brandSide={{
          title: 'התחלנו',
          body: 'מעבירים אותך לדף הבית. שם נגדיר את הארגון שלך.',
        }}
      >
        <div className={cn(styles.verifyIcon, styles.ok)}>
          <Check size={36} />
        </div>
        <p className={cn(styles.sub, styles.verifyCenter)}>
          החשבון אומת בהצלחה. מעביר לדף הבית בעוד{' '}
          <strong className="num">{String(countdown)}</strong> שנ׳...
        </p>
      </AuthShell>
    );
  }

  if (status === 'unsupported') {
    return (
      <AuthShell
        title="קישור לא תקין"
        brandSide={{
          title: 'משהו לא הסתדר',
          body: 'הקישור הזה לא תואם לאף תהליך מוכר.',
        }}
      >
        <p className={styles.formError}>סוג קישור לא נתמך.</p>
        <Link to="/auth/login" className={styles.primary}>
          התחבר
        </Link>
      </AuthShell>
    );
  }

  // expired
  return (
    <AuthShell
      title="הקישור פג תוקף"
      subtitle="קישורי אימות תקפים 24 שעות"
      brandSide={{
        title: 'בקש קישור חדש',
        body: 'התחבר שוב או צור חשבון מחדש כדי לקבל קישור טרי.',
      }}
    >
      <p className={styles.formError}>הקישור פג תוקף או כבר היה בשימוש.</p>
      <Link to="/auth/login" className={styles.primary}>
        התחבר
      </Link>
      <p className={styles.switch}>
        עוד לא רשום?{' '}
        <Link to="/auth/signup" className={styles.link}>
          צור חשבון מחדש
        </Link>
      </p>
    </AuthShell>
  );
}
